/**
 * Logging - provides logging and action recording functionality
 */

import { $dom } from './DOM';
import { Finder } from './Finder';
import { GetDestinationRoomName, roomDisplayName } from './Utilities';
import { CurrentRoomGuid } from './Constants';
import { Player } from '../models/Player';
import { Room } from '../models/Room';
import { Character } from '../models/Character';

export const Logger = {
    level: 0,

    log: function (...args: any[]): void {
        if (this.level > 0) {
            console.log.apply(console, args);
        }
    },

    logExecutingAction: function (action: any): void {
        Logger.log(
            'ACTION:',
            action.name
        );
    },

    logExecutingCommand: function (command: any, part2: string, part3: string, part4: string): void {
        Logger.log(
            'COMMAND:',
            [
                command.cmdtype,
                part2,
                part3,
                part4
            ].join(':'),
            'Executing'
        );
    },

    logExecutingTimer: function (timer: any): void {
        Logger.log(
            'TIMER:',
            timer.Name
        );
    },

    logEvaluatedCondition: function (condition: any, passed: boolean): void {
        Logger.log(
            'CONDITION:',
            condition.conditionname,
            passed ? 'Passed' : 'Failed'
        );
    }
};

Logger.level = 0;

export const ActionRecorder = {
    actions: [] as string[],

    addAction: function (...args: any[]): void {
        const method = args[0];
        const actionArgs = Array.prototype.slice.call(args, 1);
        let actionString: string;
        if (actionArgs.length === 0) {
            actionString = method;
        } else {
            const escapedArgs = actionArgs.map((arg: any) => {
                return "'" + String(arg).replace(/'/g, '\\\'') + "'";
            }).join(', ');
            actionString = method + ' ' + escapedArgs;
        }
        this.actions.push(actionString);
    },

    clear: function (): void {
        this.actions = [];
    },

    toString: function (): string {
        return this.actions.join("\n");
    },

    roomAndExits: function (): string {
        const roomTitle = String($dom('#RoomTitle').text());
        const exits: [string, string][] = [];
        const directions = ['NorthWest', 'North', 'NorthEast', 'East', 'SouthEast', 'South', 'SouthWest', 'West', 'Up', 'Down', 'In', 'Out'];
        directions.forEach((direction) => {
            const activeDir = $dom('.compass-direction.active[data-direction=' + direction + ']');
            if (activeDir.length > 0) {
                const destName = GetDestinationRoomName(direction);
                const destRoom = roomDisplayName(destName ? Finder.room(destName) : undefined);
                exits.push([direction, destRoom]);
            }
        });
        const lines: string[] = [];
        lines.push('"' + roomTitle.replace(/"/g, '\"') + '" => {');
        exits.forEach((pair, ix) => {
            const maybeComma = ix < exits.length - 1 ? ',' : '';
            lines.push('  "' + pair[0].replace(/"/g, '\"') + '" => "' + pair[1].replace(/"/g, '\"') + '"' + maybeComma);
        });
        lines.push('},');
        return lines.join("\n");
    },

    locationChange: function (direction: string): void {
        this.addAction('go_direction', direction);
    },

    roomEntered: function (roomTitle: string): void {
        this.addAction('go_to_room', roomTitle);
    },

    clickedContinue: function (): void {
        this.addAction('continue_until_unpaused');
    },

    choseInputAction: function (action: string): void {
        this.addAction('choose_input_action', action);
    },

    filledInTextInput: function (input: string): void {
        this.addAction('choose_input_action', input);
    },

    actedOnSelf: function (action: string): void {
        this.addAction('act_on_self', action);
    },

    actedOnRoom: function (action: string): void {
        this.addAction('act_on_room', action);
    },

    actedOnObject: function (object: any, action: string): void {
        let method: string;
        let objectName: string;
        if (object instanceof Player) {
            return this.actedOnSelf(action);
        } else if (object instanceof Room) {
            return this.actedOnRoom(action);
        } else if (object instanceof Character) {
            method = 'act_on_character';
            objectName = object.CharnameOverride || object.Charname;
        } else {
            method = 'act_on_object';
            objectName = object.sdesc || object.name;
        }
        this.addAction(method, objectName, action);
    }
};

export const ImageRecorder = {
    imagesSeen: {} as { [key: string]: boolean },

    sawImage: function (image: string): void {
        this.imagesSeen[image] = true;
    },

    allSeenImages: function (): string[] {
        return Object.keys(this.imagesSeen);
    }
};

// Make available globally
(window as any).Logger = Logger;
(window as any).ActionRecorder = ActionRecorder;
(window as any).ImageRecorder = ImageRecorder;
